import { useEffect, useRef } from 'react';
import { attemptAsync } from 'es-toolkit/util';
import { useAuthState } from 'state/useAuthState';
import { useMapPlacesState } from 'state/useMapPlacesState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useApiPlaces } from './useApiPlaces';

/**
 * Once logged in, merges places saved locally (e.g. created while logged out)
 * into the user's API places, then clears local places. Mount once at app level.
 *
 * Places are matched by id rather than timestamp: places saved while logged out
 * can be older than the API data.
 */
export const useMigrateLocalPlaces = (): void => {
  const enabled = useReleaseFlag('places_server_source_of_truth');
  const { isAuthenticated } = useAuthState();
  const { places: localPlaces, clearMapPlaces } = useMapPlacesState();
  const { saveMultipleMapPlacesToAPI, loading } = useApiPlaces({ enabled });
  const migratingRef = useRef(false);

  useEffect(() => {
    if (
      !enabled ||
      !isAuthenticated ||
      loading ||
      !localPlaces.length ||
      migratingRef.current
    ) {
      return;
    }

    const migrate = async (): Promise<void> => {
      migratingRef.current = true;
      const [error] = await attemptAsync(() =>
        saveMultipleMapPlacesToAPI(localPlaces),
      );
      migratingRef.current = false;
      // On failure, local places are kept so the migration can retry
      if (!error) clearMapPlaces();
    };
    migrate();
  }, [
    saveMultipleMapPlacesToAPI,
    clearMapPlaces,
    enabled,
    isAuthenticated,
    loading,
    localPlaces,
  ]);
};
